import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Add } from "@mui/icons-material";
import { Chip, TableCell, TableRow, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import noImporterImg from "../../../assets/images/placeholders/noImportersPlaceholderImg.svg";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import messages from "../../../assets/locale/messages";
import Button from "../../../components/Button";
import EmptyState from "../../../components/EmptyState";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import Pagination from "../../../components/Pagination";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import TruncateText from "../../../components/TruncateText";
import { employeesListRequest } from "../../../store/Employees/actions";
import { rolesLookupRequest } from "../../../store/Lookups/actions";
import { CHIPS_CLASSES } from "../../../utils/Constants";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import { hasPermission, PERMISSIONS_IDS } from "../../../utils/Roles";
import "../Employees.scss";

const EmployeesList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const { employees } = messages[lang];

  const employeesMeta = useSelector(
    (state) => state.employees.employeesList.meta
  );

  const employeesList = useSelector(
    (state) => state.employees.employeesList.list
  );

  const rolesLookup = useSelector(
    (state) => state.lookups.roles.list
  );

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilterValue, setStatusFilterValue] = useState([]);
  const [roleFilterValue, setRoleFilterValue] = useState([]);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [clearFilters, setClearFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    role_ids: [],
    isActive: null
  });

  useEffect(() => {
    dispatch(rolesLookupRequest());
  }, [dispatch]);

  const requestEmployeesList = () => {
    dispatch(
      employeesListRequest({
        params: {
          page,
          search_value: searchValue,
          ...paramsObject
        }
      })
    );
  };

  useEffect(() => {
    requestEmployeesList();
  }, [page, searchValue]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestEmployeesList();
  }, [paramsObject]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        role_ids: roleFilterValue,
        isActive:
          !statusFilterValue.length || statusFilterValue.length === 2
            ? null
            : statusFilterValue.includes(1)
      });

      setIsApplyFilters(false);
    }
  }, [isApplyFilters, roleFilterValue, statusFilterValue]);

  useEffect(() => {
    if (clearFilters) {
      setRoleFilterValue([]);
      setStatusFilterValue([]);
      setParamsObject({
        role_ids: [],
        isActive: null
      });
      setClearFilters(false);
    }
  }, [clearFilters]);

  const EMPLOYEE_STATUS_FILTER = [
    { Id: 1, Name: employees.status.active },
    { Id: 2, Name: employees.status.inActive }
  ];

  const renderFilterContent = () => (
    <div className=" h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={rolesLookup}
          name="employeesRole"
          label={employees.employeesList.filters.role}
          searchPlaceholder={
            employees.employeesList.filters.searchPlaceholders.role
          }
          selectedOptionsIds={roleFilterValue}
          setSelectedOptionsIds={setRoleFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={EMPLOYEE_STATUS_FILTER}
          name="employeesStatus"
          label={employees.employeesList.filters.status}
          selectedOptionsIds={statusFilterValue}
          setSelectedOptionsIds={setStatusFilterValue}
          clearFilters={clearFilters}
          collabsible
          filterModalOpen={filterModalOpen}
        />
      </div>
    </div>
  );

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const handleStatusClass = (status) => {
    let chipClass;
    if (status === employees.status.active) {
      chipClass = CHIPS_CLASSES.green;
    } else {
      chipClass = CHIPS_CLASSES.lightBrown;
    }
    return chipClass;
  };

  const renderEmployeesTableRow = ({
    Name,
    Email,
    Image,
    RoleName,
    Status,
    Id
  }) => {
    const chipClass = handleStatusClass(Status);
    return (
      <TableRow
        className="pointer"
        key={`request-row-${Id}`}
        onClick={() =>
          navigate(ROUTE_PATHS.employeeDetails.replace(":id", Id))
        }>
        <TableCell>
          <div className="d-flex align-items-center gap-3">
            <img
              src={Image || noRequestImg}
              onError={onImageError}
              alt={"employee-image"}
              className="employee-img faint-grey-border border-radius-8 object-fit-cover"
            />
            <TruncateText text={Name || "__"} />
          </div>
        </TableCell>
        <TableCell>
          <TruncateText text={Email || "__"} />
        </TableCell>
        <TableCell>
          <TruncateText text={RoleName || "__"} />
        </TableCell>
        <TableCell>
          <Chip
            label={Status}
            className={`border-radius-8 fw-bold ${chipClass}`}
            sx={{ fontWeight: "semibold" }}
          />
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="exporters-list-container">
      <div className="page-title pb-4 d-flex justify-content-between align-items-md-center align-items-start">
        <div className="title-count-container d-flex justify-content-center align-items-md-center align-items-start flex-column flex-md-row">
          <div>
            <Typography variant="h5" className="title-color me-1">
              {employees.employeesList.employeesListTitle}
            </Typography>
          </div>
          <div className="block-title text-secondary">
            <Typography>{`(${employeesMeta?.count ? employeesMeta?.count : "0"} ${employeesMeta?.count === 1 ? employees.employeesList.employee : employees.employeesList.employees})`}</Typography>
          </div>
        </div>
        <div className="title-buttons-container d-flex gap-sm-3 gap-1 align-items-center">
          <Filters
            handleClearFilters={setClearFilters}
            handleApplyFilters={(action) => {
              setPage(1);
              setIsApplyFilters(action);
            }}
            FilterContent={renderFilterContent()}
            setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
          />
          {hasPermission(PERMISSIONS_IDS.employees_add) && (
            <Button
              label={
                <span className="d-flex align-items-center">
                  <Add fontSize="medium" />
                  <span className="ps-1 d-none d-md-block">
                    {employees.employeesList.addMoreEmployees}
                  </span>
                </span>
              }
              onClick={() => navigate(ROUTE_PATHS.addEmployees)}
              customSpacing={"px-2 px-md-4"}
            />
          )}
        </div>
      </div>
      <div className="employee-table-container bg-white d-flex flex-column w-100 p-4 pb-0 px-0 border-radius-8">
        <div className="table-header d-flex px-4 align-items-center w-100 mb-4">
          <SearchInput
            name="EmployeeSearch"
            id="EmployeeSearch"
            value={searchValue}
            inputClass="border-radius-8"
            inputWrapperClass="search-input-wrapper"
            placeholder={employees.employeesList.searchPlaceholder}
            onChange={(value) => {
              setPage(1);
              setSearchValue(value);
            }}
          />
        </div>
        <div className="table-content">
          <div>
            {employeesList?.length ? (
              <Table
                className={"px-0"}
                headlines={[
                  employees.employeesList.tableHeader.employeeName,
                  employees.employeesList.tableHeader.email,
                  employees.employeesList.tableHeader.role,
                  employees.employeesList.tableHeader.status
                ]}
                rows={
                  !isLoading &&
                  employeesList &&
                  employeesList.map((employee) =>
                    renderEmployeesTableRow(employee)
                  )
                }
              />
            ) : (
              <div className="border-top">
                {!isLoading && (
                  <EmptyState
                    subTitle={
                      (!!searchValue ||
                        !!statusFilterValue.length ||
                        !!roleFilterValue.length) &&
                      !employeesList.length
                        ? employees.employeesList.noSearchResults
                        : employees.employeesList.noEmployeesFound
                    }
                    imgSrc={noImporterImg}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
          {!isLoading && employeesList.length > 0 && (
            <Pagination
              count={employeesMeta?.pages}
              page={page || employeesMeta.currentPage}
              handleChange={(page) => setPage(page)}
              defaultPage={1}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeesList;
